
import Player from '../objects/Player.js';
import Friend from '../objects/Friend.js';
import OverlayUI from '../ui/OverlayUI.js';
import * as fontConfig from '../scripts/FontConfig.js';


export default class VillageScene extends Phaser.Scene {
  constructor() {
    super('VillageScene');
  }

  create(data) {
    // Reset scene transition flag
    this.sceneTransitioning = false;
    
    // Initialize door collision state tracking
    this.doorCollisionState = {
      isColliding: false,
      previouslyColliding: false,
      currentDoorId: null,
      currentFriendName: null
    };

    // Create persistent modal (hidden by default)
    this.createPersistentModal();
    
    // Persistent data object for player position
    if (!window.hypnoBreakerData) window.hypnoBreakerData = {};
    const lastPos = window.hypnoBreakerData.lastPlayerPos;


    // Map size: matches Tiled map (128x64 tiles * 16px = 2048x1024)
    this.mapWidth = 2048;
    this.mapHeight = 1024;
    this.cameras.main.setBounds(0, 0, this.mapWidth, this.mapHeight);
    this.physics.world.setBounds(0, 0, this.mapWidth, this.mapHeight);

    // Create Tiled map
    const map = this.make.tilemap({ key: 'map' });
    
    // Add tileset - match the external tileset name
    const tileset = map.addTilesetImage('modern', 'tileset');
    const ui = map.addTilesetImage('ui', 'ui');
    const tilesets = [tileset, ui];

    // Check if all friends have been rescued for final scene
    const rescuedFriends = this.loadRescuedFriends();
    const allFriendsRescued = rescuedFriends.length >= 5;
    
    console.log(`Rescued friends: ${rescuedFriends.join(', ')} (${rescuedFriends.length}/5)`);
    if (allFriendsRescued) {
      console.log('🎯 ALL 5 FRIENDS RESCUED - Checking if ready for final scene...');
      
      // Get group information
      const group = window.sessionManager ? window.sessionManager.group : (localStorage.getItem('hypnobreaker_group') || '2');
      const isGroup1 = parseInt(group) === 1;
      const fromReflection = data && data.fromReflection === true;
      
      console.log(`Group: ${group}, From Reflection: ${fromReflection}`);
      
      // Only trigger final scene if:
      // - Group 1 (skips reflections entirely), OR
      // - Group 2 AND came from ReflectionScene
      if (isGroup1 || fromReflection) {
        console.log('✅ CONDITIONS MET - PREPARING FINAL SCENE');
        this.isFinalScene = true;
      } else {
        console.log('⚠️ Group 2 player without reflection completion - final scene deferred');
        this.isFinalScene = false;
      }
    } else {
      this.isFinalScene = false;
    }
    
    // Store whether this is the final scene
    this.isFinalScene = allFriendsRescued;
    
    // Store layer references for collision setup
    this.collisionLayers = [];
    
    // Create map layers in proper order (bottom to top)
    try {
      // 1. Ground layer (bottom-most, no collision)
      const groundLayer = map.createLayer('Ground', tilesets, 0, 0);
      
      // 2. Ground decoration (no collision)
      const groundDecorationLayer = map.createLayer('GroundDecoration', tilesets, 0, 0);
      
      // 3. Ground decoration with collision
      const groundDecorationCollidesLayer = map.createLayer('GroundDecorationCollides', tilesets, 0, 0);
      if (groundDecorationCollidesLayer && !this.isFinalScene) {
        groundDecorationCollidesLayer.setCollisionByExclusion([-1]); // Fallback: collide all except empty
        this.collisionLayers.push(groundDecorationCollidesLayer);
      }
      
      // 4. Buildings layer (with collision)
      const buildingsLayer = map.createLayer('Buildings', tilesets, 0, 0);
      if (buildingsLayer && !this.isFinalScene) {
        buildingsLayer.setCollisionByExclusion([-1]); // Fallback: collide all except empty
        this.collisionLayers.push(buildingsLayer);
      }
      
      // 5. Building decorations (with collision)
      const buildingDecorationsLayer = map.createLayer('BuildingsDecoration', tilesets, 0, 0);
      if (buildingDecorationsLayer && !this.isFinalScene) {
        buildingDecorationsLayer.setCollisionByExclusion([-1]); // Fallback: collide all except empty
        this.collisionLayers.push(buildingDecorationsLayer);
      }
      
      // 6. Above layer (renders above player, no collision)
      const aboveLayer = map.createLayer('Above', tilesets, 0, 0);
      if (aboveLayer) {
        aboveLayer.setDepth(1000); // Render above player and other objects
      }
      
      // 7. Handle object layers (doors, triggers, etc.)
      if(!this.isFinalScene) this.setupObjectLayers(map);
      
      console.log('Tiled map loaded successfully with layers:', map.layers.map(layer => layer.name));
      console.log('Collision layers setup:', this.collisionLayers.length);
                        
    } catch (error) {
      console.error('Error loading Tiled map:', error);
      console.log('Available layers:', map.layers.map(layer => layer.name));
      
    }   
    
    // Player: using enhanced Player class
    let px = 1450, py = 550; // Default spawn position
    
    // If all friends rescued, always spawn at default location for final scene
    if (allFriendsRescued) {
      px = 1450;
      py = 550;
      console.log('All friends rescued! Setting up final scene at default location');
    } else if (lastPos && typeof lastPos.x === 'number' && typeof lastPos.y === 'number') {
      px = lastPos.x;
      py = lastPos.y + 10; // spawn 10px below last position
      console.log(`Spawning player at last position: (${px}, ${py})`);
    } else {
      console.log(`Spawning player at default position: (${px}, ${py})`);
    }
    
    this.player = new Player(this, px, py);
    

    // Set player depth to render above ground layers but below "Above" layer
    this.player.setDepth(500);
    
    // Set up collision between player and all collision layers
    this.collisionLayers.forEach((layer, index) => {
      this.physics.add.collider(this.player, layer, (player, tile) => {
        // console.log(`Player collided with tile in layer ${layer.layer.name}:`, tile.index);
      });
    });

    // Initialize friends system
    this.friends = [];
    this.setupRescuedFriends();

    // Set up collision between player and door hitboxes (if they exist)
    if (this.doorHitboxes && this.doorHitboxes.children.size > 0 && this.isFinalScene === false) {
      console.log(`Setting up overlap detection for ${this.doorHitboxes.children.size} door hitboxes`);
      
      // Add debugging to the overlap setup
      const overlapCollider = this.physics.add.overlap(
        this.player, 
        this.doorHitboxes, 
        (player, doorHitbox) => {
          // Wrap the callback with additional safety checks
          try {
            this.onPlayerEnterDoor(player, doorHitbox);
          } catch (error) {
            console.error('Error in door overlap callback:', error);
            console.log('Player state:', {
              exists: !!player,
              hasBody: !!player?.body,
              active: player?.active,
              sceneKey: player?.scene?.scene?.key
            });
            console.log('DoorHitbox state:', {
              exists: !!doorHitbox,
              hasBody: !!doorHitbox?.body,
              active: doorHitbox?.active,
              sceneKey: doorHitbox?.scene?.scene?.key
            });
          }
        }, 
        null, 
        this
      );
      
      // Store reference to the collider for cleanup
      this.doorOverlapCollider = overlapCollider;
      
    } else {
      console.log('No door hitboxes found or empty group');
    }

    // Camera follows player
    this.cameras.main.startFollow(this.player, true, 0.08, 0.08);
    
    // Add fade-in transition if coming from another scene
    if (data && data.fromBuilding) {
      this.createSceneTransition();
    }
    
    // Create persistent overlay UI with navigation buttons
    this.overlayUI = new OverlayUI(this, { showNavigation: true });
    this.overlayUI.create();
    
    // Check if a friend was just rescued and update overlay
    if (data && data.fromFriend && data.success && data.friendName) {
      this.overlayUI.rescueFriend(data.friendName);
      // Refresh friends to include the newly rescued friend
      this.refreshFriends();
      
      // Check if this rescue completed the game (all 5 friends rescued)
      const currentRescued = this.loadRescuedFriends();
      if (currentRescued.length >= 5 && !this.isFinalScene) {
        console.log('🎯 5th friend rescued! Triggering final scene IN PLACE...');
        // Convert to final scene mode without restarting
        this.convertToFinalScene();
        return; // Don't continue with normal scene flow
      }
    } else if (data && (data.fromFriend || data.fromBuilding)) {
      // Always refresh friends when returning from other scenes
      this.refreshFriends();
    }

    // Listen for scene resume events (in case scene is paused/resumed)
    this.events.on('resume', () => {
      console.log('VillageScene resumed, refreshing friends');
      this.refreshFriends();
    });

    // Debug shortcuts for testing
    this.setupDebugShortcuts();

  }

  setupDebugShortcuts() {
    // Create keyboard input
    this.cursors = this.input.keyboard.createCursorKeys();
    
    // Add T key for testing ReflectionScene
    this.tKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.T);
    
    // Add Q key for testing (rescue 4 friends)
    this.qKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.Q);
    
    // Add F key for testing final scene (rescue all 5 friends)
    this.fKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.F);
    
    // Add R key to reset (clear all rescued friends)
    this.rKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.R);
    
    // Set up key press handlers
    this.tKey.on('down', () => {
      console.log('🔧 DEBUG: T pressed - launching ReflectionScene for mo');
      
      // Create test game data for reflection scene
      const testGameData = {
        friend: 'mo',
        startTime: Date.now(),
        answers: [
          { id: 1, text: 'wat ben je aan het doen?', response: 'gamen natuurlijk 👾', timing: 2500 },
          { id: 2, text: 'hoe lang ben je al aan het gamen?', response: 'Nog maar net! Ik begon na school', timing: 3200 }
        ],
        reflections: []
      };
      
      // Launch reflection scene with test data
      this.scene.start('ReflectionScene', {
        friendName: 'mo',
        success: true,
        gameData: testGameData
      });
    });
    
    this.qKey.on('down', () => {
      console.log('🔧 DEBUG: Q pressed - rescuing 4/5 friends');
      const fourFriends = ['mo', 'julia', 'sam', 'liam']; // Missing louisa
      localStorage.setItem('hypnobreaker_rescued_friends', JSON.stringify(fourFriends));
      
      // Update overlay UI
      if (this.overlayUI) {
        fourFriends.forEach(friendName => {
          this.overlayUI.rescueFriend(friendName);
        });
      }
      
      // Refresh friends in scene
      this.refreshFriends();
      
      console.log('4 friends rescued. Rescue Louisa to trigger final scene!');
    });
    
    // this.fKey.on('down', () => {
    //   console.log('🔧 DEBUG: F pressed - rescuing ALL 5 friends (FINAL SCENE)');
    //   const allFriends = ['mo', 'julia', 'sam', 'liam', 'louisa'];
    //   localStorage.setItem('hypnobreaker_rescued_friends', JSON.stringify(allFriends));
      
    //   // Clear last position to force default spawn for final scene
    //   if (window.hypnoBreakerData) {
    //     window.hypnoBreakerData.lastPlayerPos = null;
    //   }
      
    //   // Update overlay UI
    //   if (this.overlayUI) {
    //     allFriends.forEach(friendName => {
    //       this.overlayUI.rescueFriend(friendName);
    //     });
    //   }
      
    //   // Force reload the scene to trigger final scene mode
    //   console.log('🎯 Reloading scene to trigger FINAL SCENE...');
    //   this.scene.restart();
    // });
    
    this.rKey.on('down', () => {
      console.log('🔧 DEBUG: R pressed - resetting all rescued friends');
      localStorage.removeItem('hypnobreaker_rescued_friends');
      
      // Reload scene to clear friends
      this.scene.restart();
      console.log('All friends reset!');
    });
  }

  shutdown() {
    // Clean up event listeners
    this.events.off('resume');
    
    // Clean up friends
    if (this.friends) {
      this.friends.forEach(friend => {
        friend.stopFollowing();
        friend.destroy();
      });
      this.friends = [];
    }
    
    // Clean up overlay UI
    if (this.overlayUI) {
      this.overlayUI.destroy();
      this.overlayUI = null;
    }
    
    // Clean up physics groups and prevent further collision detection
    if (this.doorHitboxes) {
      this.doorHitboxes.clear(true, true);
    }
    
    // Remove all physics colliders/overlaps
    if (this.physics && this.physics.world) {
      this.physics.world.removeAllListeners();
    }
    this.player.destroy();
    this.player = null;
    
    this.sceneTransitioning = false;
  }

  setupObjectLayers(map) {
    // Create physics group for door hitboxes
    this.doorHitboxes = this.physics.add.staticGroup();
    
    // Get the Doors-hitbox object layer
    const doorObjectLayer = map.getObjectLayer('Doors-hitbox');
    
    if (doorObjectLayer) {
      console.log(`Found Doors-hitbox layer with ${doorObjectLayer.objects.length} objects`);
      
      // Convert each object to a physics body
      doorObjectLayer.objects.forEach((obj, index) => {
        // Create invisible physics rectangle for each door object
        const doorHitbox = this.add.rectangle(obj.x, obj.y, obj.width, obj.height);
        doorHitbox.setOrigin(0, 0); // Tiled objects use top-left origin
        doorHitbox.setVisible(false); // Make invisible
        doorHitbox.setAlpha(0); // Ensure completely invisible
        
        // Add physics body
        this.physics.add.existing(doorHitbox, true); // true = static body
        
        // Store door properties from Tiled
        doorHitbox.doorData = {
          id: obj.id,
          name: obj.name || `door_${index}`,
          type: obj.type || 'door',
          x: obj.x,
          y: obj.y,
          width: obj.width,
          height: obj.height
        };
        
        // Add to door hitboxes group
        this.doorHitboxes.add(doorHitbox);
        
        console.log(`Created door hitbox: ${doorHitbox.doorData.name} at (${obj.x}, ${obj.y})`);
      });
    } else {
      console.log('No Doors-hitbox object layer found');
    }
  }

  onPlayerEnterDoor(player, doorHitbox) {
    // Prevent multiple simultaneous scene transitions
    if (this.sceneTransitioning) {
      console.log('Scene already transitioning, ignoring door entry');
      return;
    }
    
    // Check if objects are still valid
    if (!player || !doorHitbox || !player.body || !doorHitbox.body) {
      console.warn('Invalid objects in door collision - ignoring');
      return;
    }
    
    // Check if objects belong to this scene
    if (player.scene !== this || doorHitbox.scene !== this) {
      console.warn('Objects from different scene - ignoring');
      return;
    }
    
    // This function is called when player overlaps with a door hitbox
    const doorData = doorHitbox.doorData;
    const doorId = doorData?.id || doorData?.name || 'unknown';
    
    // Update collision state
    this.doorCollisionState.isColliding = true;
    this.doorCollisionState.currentDoorId = doorId;
    
    // Extract friend name from door name (e.g., "room-julia" -> "julia")
    const doorName = doorData?.name?.toLowerCase();
    let friendName = null;
    if (doorName) {
      let friendMatch = doorName.match(/room[_-](\w+)/);
      if (!friendMatch) {
        friendMatch = doorName.match(/door[_-]?(\w+)/);
      }
      if (!friendMatch) {
        friendMatch = doorName.match(/(\w+)/);
      }
      if (friendMatch) {
        friendName = friendMatch[1];
      }
    }
    
    this.doorCollisionState.currentFriendName = friendName;
    
    // Check if this friend has already been rescued
    const rescuedFriends = this.loadRescuedFriends();
    const isRescued = friendName && rescuedFriends.includes(friendName);
    
    // Handle state transitions - only act on first collision, not continuous overlaps
    if (!this.doorCollisionState.previouslyColliding) {
      // Just started colliding
      if (isRescued) {
        // console.log(`🚫 Friend ${friendName} already rescued, showing modal`);
        this.showPersistentModal(friendName);
        return; // Don't proceed to room
      } else {
        // Friend not rescued, proceed to room
        // console.log(`✅ Friend ${friendName} not rescued, proceeding to room`);
        this.proceedToRoom(doorData, player);
      }
    } else {
      // Still colliding, maintain state but don't take action
      // console.log(`🔄 Still colliding with ${friendName}'s door`);
    }
  }

  proceedToRoom(doorData, player) {
    // Set transition flag immediately to prevent multiple triggers
    this.sceneTransitioning = true;
    console.log('Door entry triggered, setting scene transitioning to true');
  
    // Save player position
    window.hypnoBreakerData.lastPlayerPos = { x: player.x, y: player.y };
    
    // Use Phaser's native camera fade out
    this.cameras.main.fadeOut(500, 0, 0, 0);
    
    // Listen for fade out complete event
    this.cameras.main.once(
      Phaser.Cameras.Scene2D.Events.FADE_OUT_COMPLETE,
      () => {
        // Switch to BuildingScene after fade completes
        this.scene.start('BuildingScene', { doorData: doorData });
      }
    );
  }

  createPersistentModal() {
    // Get screen center for initial positioning (will be updated when shown)
    const centerX = this.cameras.main.width / 2;
    const centerY = this.cameras.main.height / 2;
    
    // Create modal elements (initially hidden)
    this.persistentModalBox = this.add.rectangle(centerX, centerY, 200, 100, 0xeeeeee, 1);
    this.persistentModalBox.setDepth(1901);
    this.persistentModalBox.setVisible(false);
    
    this.persistentModalText = fontConfig.createText(this, centerX, centerY, '', "default", {});
    this.persistentModalText.setOrigin(0.5, 0.5);
    this.persistentModalText.setDepth(1902);
    this.persistentModalText.setVisible(false);
  }

  showPersistentModal(friendName) {
    // Update modal position to current camera center
    const cameraX = this.cameras.main.scrollX;
    const cameraY = this.cameras.main.scrollY;
    const centerX = cameraX + (this.cameras.main.width / 2);
    const centerY = cameraY + (this.cameras.main.height / 2);
    
    // Update positions and text
    this.persistentModalBox.setPosition(centerX, centerY);
    this.persistentModalText.setPosition(centerX, centerY);
    this.persistentModalText.setText(`${friendName} is al buiten`);
    
    // Fade in if not already visible
    if (!this.persistentModalBox.visible) {
      this.persistentModalBox.setVisible(true);
      this.persistentModalText.setVisible(true);
      this.persistentModalBox.setAlpha(0);
      this.persistentModalText.setAlpha(0);
      
      this.tweens.add({
        targets: [this.persistentModalBox, this.persistentModalText],
        alpha: 1,
        duration: 200,
        ease: 'Power2.easeOut'
      });
    }
  }

  hidePersistentModal() {
    if (this.persistentModalBox.visible) {
      this.tweens.add({
        targets: [this.persistentModalBox, this.persistentModalText],
        alpha: 0,
        duration: 200,
        ease: 'Power2.easeOut',
        onComplete: () => {
          this.persistentModalBox.setVisible(false);
          this.persistentModalText.setVisible(false);
        }
      });
    }
  }

  showFriendAlreadyRescuedModal(friendName) {
    // Get current camera position for modal positioning
    const cameraX = this.cameras.main.scrollX;
    const cameraY = this.cameras.main.scrollY;
    const centerX = cameraX + (this.cameras.main.width / 2);
    const centerY = cameraY + (this.cameras.main.height / 2);
    
   
    // Create modal content box
    const modalBox = this.add.rectangle(centerX, centerY, 400, 150, 0xffffff, 1);
    modalBox.setDepth(1901); // Above background

    
    // Create modal text
    const modalText = fontConfig.createText(this, centerX, centerY, `${friendName} is al buiten`, "default", {});
    modalText.setOrigin(0.5, 0.5);
    modalText.setDepth(1902); // Above modal box
    
    // Group modal elements for easy management
    const modalElements = [modalBox, modalText];
    
    // Fade out and destroy modal after 1 second
    this.time.delayedCall(1000, () => {
      // Fade out animation
      this.tweens.add({
        targets: modalElements,
        alpha: 0,
        duration: 300,
        ease: 'Power2.easeOut',
        onComplete: () => {
          // Destroy all modal elements
          modalElements.forEach(element => {
            if (element && element.destroy) {
              element.destroy();
            }
          });
          console.log(`Modal for ${friendName} closed`);
        }
      });
    });
    
    console.log(`Showed modal notification: ${friendName} is al buiten`);
  }

  onEnterBuilding(player, door) {
   
  }



  createSceneTransition() {
    // Use Phaser's native camera fade in
    this.cameras.main.fadeIn(300, 0, 0, 0);
  }

  createFallbackBackground() {
    // Create a simple green background if Tiled map fails
    const graphics = this.add.graphics();
    graphics.fillStyle(0x4a7c59, 1); // Green color
    graphics.fillRect(0, 0, this.mapWidth, this.mapHeight);
    console.log('Created fallback background');
  }

  setupRescuedFriends() {
    // Load rescued friends from localStorage
    const rescued = this.loadRescuedFriends();
    
    // Different positioning for final scene vs normal gameplay
    let spawnOffsets;
    
    if (this.isFinalScene) {
      // Final scene: arrange friends in a circle around the player
      console.log('Setting up final scene - arranging friends in circle around player');
      spawnOffsets = [
        { x: -80, y: 0 },    // mo (left)
        { x: 80, y: 0 },     // julia (right)
        { x: 0, y: -80 },    // sam (top)
        { x: -56, y: 56 },   // liam (bottom-left)
        { x: 56, y: 56 }     // louisa (bottom-right)
      ];
    } else {
      // Normal gameplay: friends follow behind player
      spawnOffsets = [
        { x: -64, y: 32 },   // mo
        { x: 64, y: 32 },    // julia
        { x: -64, y: -32 },  // sam
        { x: 64, y: -32 },   // liam
        { x: 0, y: 64 }      // louisa
      ];
    }
    
    // Friend name to sprite key mapping (using spritesheet keys for animations)
    const friendSprites = {
      'mo': 'mo',
      'julia': 'julia',
      'sam': 'sam',
      'liam': 'liam',
      'louisa': 'louisa'
    };
    
    rescued.forEach((friendName, index) => {
      const spriteKey = friendSprites[friendName];
      if (spriteKey && spawnOffsets[index]) {
        const spawnX = this.player.x + spawnOffsets[index].x;
        const spawnY = this.player.y + spawnOffsets[index].y;
        
        const friend = new Friend(this, spawnX, spawnY, spriteKey, friendName);
        friend.setDepth(450); // Behind player but above ground
        
        if (this.isFinalScene) {
          // Final scene: friends don't follow, they stay in formation
          console.log(`Final scene: Positioned ${friendName} at (${spawnX}, ${spawnY})`);
          // TODO: Add jumping animation here later
        } else {
          // Normal gameplay: friends follow the player
          const followDistance = 10 * (index + 1);
          friend.followDistance = followDistance;
          friend.startFollowing(this.player);
        }
        
        // Set up collision with terrain
        this.collisionLayers.forEach((layer) => {
          this.physics.add.collider(friend, layer);
        });
        
        this.friends.push(friend);
        // console.log(`Spawned rescued friend: ${friendName} at (${spawnX}, ${spawnY}) ${this.isFinalScene ? '(FINAL SCENE)' : '(following)'}`);
      }
    });
    
    // If this is the final scene, trigger the celebration
    if (this.isFinalScene) {
      console.log('🎉 TRIGGERING FINAL CELEBRATION - All conditions met');
      this.triggerFinalCelebration();
    } else if (rescued.length >= 5) {
      console.log('⏳ All friends rescued but waiting for reflection completion');
    }
  }
  
  triggerFinalCelebration() {
    // console.log('🎉 FINAL SCENE TRIGGERED! All friends rescued!');
    
    // Disable player controls and movement during celebration
    if (this.player) {
      this.player.disableControls();
      if (this.player.body) {
        this.player.body.setVelocity(0, 0);
      }
    }
    
    // Stop friends from moving
    this.friends.forEach(friend => {
      if (friend.body) {
        friend.body.setVelocity(0, 0);
      }
      friend.stopFollowing();
    });
    
    // Start hopping animations with a slight delay for dramatic effect
    this.time.delayedCall(500, () => {
      console.log('🎯 Starting celebration hopping animations!');
      
      // Start player hopping
      if (this.player && this.player.startHopping) {
        this.player.startHopping();
      }
      
      // Start friends hopping with staggered timing
      this.friends.forEach((friend, index) => {
        this.time.delayedCall(index * 100, () => {
          if (friend && friend.startHopping) {
            friend.startHopping();
          }
        });
      });
    });
    
    // Fade in the final screen image after hopping starts
    this.time.delayedCall(2000, () => {
      this.showFinalScreen();
    });
    
    // Log the achievement
    console.log('🏆 GAME COMPLETED! All friends have been rescued!');
  }

  showFinalScreen() {
    // console.log('🎨 Showing final screen overlay...');
    
    // Get current camera position and world bounds
    const cameraX = this.cameras.main.scrollX;
    const cameraY = this.cameras.main.scrollY;
    const centerX = cameraX + (this.cameras.main.width / 2);
    const centerY = cameraY + (this.cameras.main.height / 2);

    
    // Check if the texture exists
    
    // Create the final screen image (800x600) centered on current camera view
    this.finalScreenImage = this.add.image(centerX, centerY, 'finalScreen');
    
    // Make it scroll with the camera so it stays centered on screen
    this.finalScreenImage.setScrollFactor(1, 1);
    
    // Set high depth to appear above everything else
    this.finalScreenImage.setDepth(2000);
    
    // Start invisible for fade-in effect
    this.finalScreenImage.setAlpha(0);
    
    // Fade in the final screen over 2 seconds
    this.tweens.add({
      targets: this.finalScreenImage,
      alpha: 1,
      duration: 2000,
      ease: 'Power2.easeIn',
      onUpdate: (tween) => {

      },
      onComplete: () => {

        // Submit final score and show button after final screen is fully visible
        this.time.delayedCall(1000, () => {
          this.submitFinalScore();
          this.showMediamastersButton();
        });
      }
    });
  }

  showMediamastersButton() {
    console.log('🔗 Showing Mediamasters return button...');
    
    // Get current camera position for button positioning
    const cameraX = this.cameras.main.scrollX;
    const cameraY = this.cameras.main.scrollY;
    const centerX = cameraX + (this.cameras.main.width / 2);
    const buttonY = cameraY + (this.cameras.main.height * 0.95); // 80% down the screen
    
    // Create button background (green rectangle)
    this.mediamastersButton = this.add.rectangle(centerX, buttonY, 300, 40, 0x27AE60); // Nice green color
    this.mediamastersButton.setDepth(2100); // Above final screen
    this.mediamastersButton.setScrollFactor(1, 1); // Follow camera
    this.mediamastersButton.setStrokeStyle(4, 0x1E8449); // Darker green border
    
    // Add button text
    this.mediamastersButtonText = this.add.text(centerX, buttonY, 'Ga terug naar MediaMasters', {
      fontFamily: 'm6',
      fontSize: '20px',
      fill: '#ffffff',
      fontStyle: 'bold'
    });
    this.mediamastersButtonText.setOrigin(0.5, 0.5);
    this.mediamastersButtonText.setDepth(2101); // Above button background
    this.mediamastersButtonText.setScrollFactor(1, 1); // Follow camera
    
    // Make button interactive
    this.mediamastersButton.setInteractive();
    
    // Add hover effects
    this.mediamastersButton.on('pointerover', () => {
      this.mediamastersButton.setFillStyle(0x2ECC71); // Lighter green on hover
      this.mediamastersButtonText.setScale(1.05); // Slightly larger text
      document.body.style.cursor = 'pointer';
    });
    
    this.mediamastersButton.on('pointerout', () => {
      this.mediamastersButton.setFillStyle(0x27AE60); // Original green
      this.mediamastersButtonText.setScale(1.0); // Original size
      document.body.style.cursor = 'default';
    });
    
    // Add click handler
    this.mediamastersButton.on('pointerdown', () => {
      console.log('🌐 Navigating to Mediamasters...');
      // Navigate to Mediamasters website
      window.open('https://game.mediamasters.nl/extra-games');
    });
    
    // Start button invisible and fade in
    this.mediamastersButton.setAlpha(0);
    this.mediamastersButtonText.setAlpha(0);
    
    // Fade in the button
    this.tweens.add({
      targets: [this.mediamastersButton, this.mediamastersButtonText],
      alpha: 1,
      duration: 500,
      ease: 'Power2.easeOut',
      onComplete: () => {
        // console.log('✅ Mediamasters button ready!');
      }
    });
  }

  async submitFinalScore() {
    console.log('📊 Submitting final game score...');
    
    try {
      // Use SessionManager to submit score and session data
      if (window.sessionManager) {
        const success = await window.sessionManager.submitFinalScore();
        if (success) {
          console.log('🎯 Final score submitted successfully!');
        } else {
          console.log('⚠️ Final score submission failed or skipped');
        }
        
        // Get complete session summary for logging
        const summary = await window.sessionManager.getSessionSummary();
        console.log('📋 Complete game session summary:', summary);
      } else {
        console.warn('❌ SessionManager not available, cannot submit score');
      }
    } catch (error) {
      console.error('💥 Error during final score submission:', error);
    }
  }

  convertToFinalScene() {
    console.log('🎯 Converting current scene to FINAL SCENE mode...');
    
    // Update the final scene flag
    this.isFinalScene = true;
    
    // Move player to default spawn position for final scene
    const defaultX = 1450;
    const defaultY = 550;
    console.log(`Moving player to default final scene position: (${defaultX}, ${defaultY})`);
    this.player.setPosition(defaultX, defaultY);
    
    // Disable collision layers for final scene (no more building interactions)
    this.collisionLayers.forEach(layer => {
      this.physics.world.removeCollider(this.player, layer);
    });
    
    // Disable door interactions
    if (this.doorOverlapCollider) {
      this.physics.world.removeCollider(this.doorOverlapCollider);
      this.doorOverlapCollider = null;
    }
    
    // Clear existing door hitboxes
    if (this.doorHitboxes) {
      this.doorHitboxes.clear(true, true);
      this.doorHitboxes = null;
    }
    
    // Stop all current friends from following
    this.friends.forEach(friend => {
      friend.stopFollowing();
      friend.body.setVelocity(0);
    });
    
    // Reposition friends in circle formation around the player's new default position
    this.repositionFriendsForFinalScene();
    
    // Trigger the celebration
    this.time.delayedCall(500, () => {
      this.triggerFinalCelebration();
    });
  }

  repositionFriendsForFinalScene() {
    console.log('Repositioning friends for final scene celebration...');
    
    const circleOffsets = [
      { x: -80, y: 0 },    // mo (left)
      { x: 80, y: 0 },     // julia (right)
      { x: 0, y: -80 },    // sam (top)
      { x: -56, y: 56 },   // liam (bottom-left)
      { x: 56, y: 56 }     // louisa (bottom-right)
    ];
    
    this.friends.forEach((friend, index) => {
      if (circleOffsets[index]) {
        const targetX = this.player.x + circleOffsets[index].x;
        const targetY = this.player.y + circleOffsets[index].y;
        
        // Smoothly tween friends to their final positions
        this.tweens.add({
          targets: friend,
          x: targetX,
          y: targetY,
          duration: 800,
          ease: 'Power2.easeOut',
          onComplete: () => {
            console.log(`${friend.friendName} moved to final position (${targetX}, ${targetY})`);
          }
        });
      }
    });
  }
  
  loadRescuedFriends() {
    // Load rescued friends from localStorage (same as OverlayUI)
    try {
      const saved = localStorage.getItem('hypnobreaker_rescued_friends');
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error loading rescued friends:', error);
      return [];
    }
  }

  refreshFriends() {
    // Clean up existing friends
    if (this.friends) {
      this.friends.forEach(friend => {
        friend.stopFollowing();
        friend.destroy();
      });
      this.friends = [];
    }
    
    // Respawn friends based on current localStorage
    this.setupRescuedFriends();
  }

  update() {
    // Check if we were colliding but are no longer colliding
    if (this.doorCollisionState.previouslyColliding && !this.doorCollisionState.isColliding) {
      // Add a small delay before hiding modal to prevent flickering from micro-gaps in collision detection
      if (!this.hideModalTimer) {
        this.hideModalTimer = this.time.delayedCall(100, () => {
          // Double-check we're still not colliding after the delay
          if (!this.doorCollisionState.isColliding) {
            this.hidePersistentModal();
            this.doorCollisionState.previouslyColliding = false;
            this.doorCollisionState.currentDoorId = null;
            this.doorCollisionState.currentFriendName = null;
          }
          this.hideModalTimer = null;
        });
      }
    } else if (this.doorCollisionState.isColliding && this.hideModalTimer) {
      // Cancel hide timer if we're colliding again
      this.hideModalTimer.remove();
      this.hideModalTimer = null;
    }
    
    // Update previous state and reset collision state for next frame
    this.doorCollisionState.previouslyColliding = this.doorCollisionState.isColliding;
    this.doorCollisionState.isColliding = false;
    
    // Player movement is handled by the Player class update method
    this.player.update();
    
    // Update all friends
    this.friends.forEach(friend => {
      friend.update();
    });
  }
}
